import React, { useRef } from 'react';
import { Glyphicon } from 'react-bootstrap';
import { connect } from 'react-redux';
import {
    setHoveringMapElementID as setHoveringMapElementIDAction,
    setSelectedMapElementID as setSelectedMapElementIDAction,
    setMultiSelectedMapElementID as setMultiSelectedMapElementIDAction,
    setDraggingData as setDraggingDataAction,
} from '../../../../actions/reduxActions/graphEditor';
import { getElementStyles } from '../elementStyles';
import { pickByPrefix, getParentOffset, getParents, getSystemFlowName } from '../../utils';

import '../../../../../css/graph/map-element.less';
import { equals, path } from 'ramda';
import MapElementBase from './MapElementBase';
import NoteElement from './notes/NoteElement';
import StandardElement from './StandardElement';
import { MAP_ELEMENT_TYPES } from '../../../../constants';
import { navigate } from '@reach/router';
import { safeToLower } from '../../../../utils/core';

export const MapElement = (props) => {

    const {
        mapElement,
        openConfig,
        hoveringMapElementId,
        setHoveringMapElementID,
        selectedElementIds,
        setSelectedMapElementID,
        setMultiSelectedMapElementID,
        dragging,
        setDraggingData,
        flowId,
        editingToken,
        doNotRender,
        currentUser,
    } = props;

    if (doNotRender) return (null);

    const { id, developerName, elementType, x, y } = mapElement;

    const styles = getElementStyles(elementType);

    const mapEl = useRef(null);
    const ports = [
        { style: styles.portLeft },
        { style: styles.portRight },
        { style: styles.portTop },
        { style: styles.portBottom },
    ];

    const parents = pickByPrefix('parent_', props);

    const parentOffset = getParentOffset(parents);

    const setSelected = newID => setSelectedMapElementID(newID);
    const setMultiSelected = newID => setMultiSelectedMapElementID(newID);
    const setHover = newID => setHoveringMapElementID(newID);

    const openDeletionSystemFlow = () => openConfig(
        'Delete Map Element',
        {
            editingToken,
            flowId,
            id,
            elementType,
        },
    );

    return (
        <MapElementBase
            // Passing components in so that any element can inherit the base dragging functionality
            component={safeToLower(mapElement.elementType) === MAP_ELEMENT_TYPES.note ? NoteElement : StandardElement}
            mapElement={mapElement}
            editingToken={editingToken}
            flowId={flowId}
            styles={styles}
            ref={mapEl}
            id={id}
            startDrag={e => setDraggingData({
                dragType: 'map',
                elementId: id,
                previousElementPosition: { x, y },
                previousMousePosition: { x: e.clientX, y: e.clientY },
            })}
            onSelect={e => e.ctrlKey ? setMultiSelected(id) : setSelected(id)}
            onOver={() => setHover(id)}
            onOut={() => setHover(null)}
            onDoubleClick={
                [MAP_ELEMENT_TYPES.return, MAP_ELEMENT_TYPES.start].some(equals(elementType.toLowerCase())) ?
                    // Returns and Starts do not have configs
                    null :
                    () => openConfig(
                        getSystemFlowName(elementType),
                        {
                            editingToken,
                            flowId,
                            id,
                            elementType,
                            x,
                            y,
                        },
                        null,
                        null,
                        null,
                        { id: elementType },
                    )
            }
            onDelete={
                [MAP_ELEMENT_TYPES.start].some(equals(elementType.toLowerCase())) ?
                    // Starts cannot be deleted
                    null :
                    openDeletionSystemFlow
            }
            isSelected={selectedElementIds.includes(id)}
            isHovered={hoveringMapElementId === id}
            isDragging={dragging && dragging.elementId === id}
            developerName={developerName}
            x={x + parentOffset.x}
            y={y + parentOffset.y}
            ports={
                ports.map(({ style }) => (
                    <foreignObject
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            setDraggingData({ dragType: 'outcome', elementId: id, previousMousePosition: { x: e.clientX, y: e.clientY } });
                        }}
                        {...style.foreignObject}
                    >
                        <Glyphicon
                            className="map-element-hover-icon"
                            glyph={style.glyph}
                        />
                    </foreignObject>
                ))
            }
            actionText={
                equals(elementType.toLowerCase(), MAP_ELEMENT_TYPES.input) ?
                    (
                        <button
                            className="map-element-description link-emulate"
                            title={path(['pageElementDeveloperName'], mapElement)}
                            onClick={() => navigate(`/${currentUser.tenantId}/pages/${path(['pageElementId'], mapElement)}`)}
                        >
                            {path(['pageElementDeveloperName'], mapElement)}
                        </button>
                    ) :
                    equals(elementType.toLowerCase(), MAP_ELEMENT_TYPES.subflow) ?
                        (
                            <button
                                className="map-element-description link-emulate"
                                title={path(['subflow', 'flowDeveloperName'], mapElement)}
                                onClick={() => navigate(`/${currentUser.tenantId}/flows/${path(['subflow', 'flowId', 'id'], mapElement)}`)}
                            >
                                {path(['subflow', 'flowDeveloperName'], mapElement)}
                            </button>
                        ) :
                        null
            }
        />
    );
};

export const mapStateToProps = ({ mapElements, groupElements, graphEditor, currentUser }, ownProps) => {
    const mapElement = mapElements.find(m => m.id === ownProps.mapElementId);

    const props = {
        mapElement,
        currentUser,
        ...ownProps,
        ...graphEditor,
    };

    let doNotRender = false;
    if (
        mapElement === undefined ||
        (mapElement.groupElementId && groupElements.find(group => group.id === mapElement.groupElementId) === undefined)
    ) {
        // Do not render if the parent does not exist
        // such as if map elements are loaded before group elements
        // or group elements are deleted before map elements
        doNotRender = true;
    } else {
        // Do not render if any ancestors have been collapsed
        getParents(mapElement, groupElements).forEach((parent) => { 
            props[`parent_${parent.id}`] = parent;
            if (parent.isOpen === false) {
                doNotRender = true;
            }
        });
    }
    props.doNotRender = doNotRender;

    return props;
};

export default connect(
    mapStateToProps,
    {
        setSelectedMapElementID: setSelectedMapElementIDAction,
        setMultiSelectedMapElementID: setMultiSelectedMapElementIDAction,
        setHoveringMapElementID: setHoveringMapElementIDAction,
        setDraggingData: setDraggingDataAction,
    },
)(MapElement);